<template>
  <v-dialog
    v-model="showDialog"
    width="720"
    content-class="primary-confirm-dialog cancel-payment-dialog elevation-12 position-relative"
    persistent
    :overlay-opacity="0.6"
    transition="primary-confirm-transition"
  >
    <loader v-if="!targetOrder || isLoading" absolute />
    <article class="cancel-payment-dialog-content">
      <h2 class="primary-confirm-dialog_title">
        {{ currentState.title }}
      </h2>
      <div v-if="paymentSummary" class="primary-confirm-dialog_source">
        {{ paymentSummary }}
      </div>
      <div class="text-center my-6">
        <component :is="stateIcon" style="max-width: 150px; max-height: 150px;"></component>
      </div>
      <div v-if="description" class="cancel-payment-dialog_description">
        {{ description }}
      </div>
      <div class="cancel-payment-dialog_actions">
        <!-- С тремя кнопками «Закрыть» ужимается до крестика: место отдаём «Вручную» и подтверждению -->
        <large-btn
          class="close-button primary-confirm-dialog_btn"
          :class="{ 'close-button__compact': showManualButton }"
          color="#f1f3f6"
          height="64px"
          :title="showManualButton ? 'Закрыть' : null"
          :uppercase="false"
          :disabled="closeDisabled"
          @click="onCloseClick"
        >
          <v-icon v-if="showManualButton">
            mdi-close
          </v-icon>
          <template v-else>
            Закрыть
          </template>
        </large-btn>
        <manual-mode-button
          v-if="showManualButton"
          class="manual-button"
          :manual="isManualState"
          :disabled="pendingCancellation || isWaiting"
          manual-hint="Вернуть деньги клиенту вручную"
          auto-hint="Вернуться к возврату через терминал"
          @toggle="toggleManualMode"
        />
        <large-btn
          class="confirm-button primary-confirm-dialog_btn primary-confirm-dialog_btn__confirm"
          color="primary"
          height="64px"
          :uppercase="false"
          :disabled="buttonDisabled"
          :loading="pendingCancellation || retryingApply"
          @click="executeAction"
        >
          <font-awesome-icon v-if="currentState.buttonIcon" :icon="currentState.buttonIcon" class="mr-2" />
          {{ currentState.buttonText }}
        </large-btn>
      </div>
      <!-- терминал выполняет возврат: закрытие окна его не отменяет, а KkmServer прервать его не умеет -->
      <primary-confirm-dialog
        v-model="showWaitingCloseConfirm"
        :persistent="false"
        title="Закрыть окно возврата?"
        subtitle="CRM не может прервать возврат на терминале. Нажмите красную кнопку на терминале. Если терминал проведёт возврат, он запишется сам."
        confirm-text="Закрыть окно"
        cancel-text="Ждать"
        @confirm="confirmCloseWhileWaiting"
      />
    </article>
  </v-dialog>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import snackbarMessage from '@/mixins/snackbar-message'
  import terminalCountdown from '@/mixins/terminal-countdown'
  import paymentApi from '@/api/order/payment'
  import PaymentType from '@/models/enums/PaymentType'
  import RefundType from '@/models/enums/RefundType'
  import { orderCancelPointError } from '@/util/order-cancel'
  import Loader from '@/components/loader/Loader.vue'
  import IconLoading from '@/components/transaction-components/IconLoading.vue'
  import IconTerminal from '@/components/icons/IconTerminal.vue'
  import IconAlertCircle from '@/components/icons/IconAlertCircle.vue'
  import IconClose from '@/components/icons/IconClose.vue'
  import LargeBtn from '@/components/ui/buttons/LargeBtn.vue'
  import PrimaryConfirmDialog from '@/components/ui/dialogs/PrimaryConfirmDialog.vue'
  import ManualModeButton from './payment-dialog/ManualModeButton.vue'
  import { getCurrency } from '@/util/money'
  import { waitForTerminalOperation } from '@/services/terminal-operation-poller'
  import { trackInBackground, untrack } from '@/services/terminal-operation-tracker'

  export default {
    name: 'CancelPaymentDialog',
    components: { Loader, LargeBtn, ManualModeButton, PrimaryConfirmDialog },
    mixins: [snackbarMessage, terminalCountdown],
    props: {
      value: Boolean,
      // карточка заказа не кладёт заказ в vuex-стор — он приходит явно пропом
      order: { type: Object, default: null },
      // возврат как шаг отмены заказа: бэк отменит заказ сам после успешного возврата
      cancelOrder: Boolean,
    },
    data () {
      return {
        isLoading: false,
        pendingCancellation: false,
        paymentTypes: [],
        currentState: null,
        poller: null,
        // операция возврата на бэке, ещё не завершённая: живёт до дедлайна и после ухода в ручной режим
        pendingOperationId: null,
        // оператор ушёл в ручной режим после провала терминала: платёж получит отметку
        manualAfterFailure: false,
        operationType: null,
        // деньги вернулись, учёт ещё пишется: причина последней неудачной записи с бэка
        applyError: null,
        // флаг отмены заказа самой операции из ответа бэка: окно, открытое «Отменить платёж»,
        // может продолжать возврат, начатый «Отменить заказ и оплату», и наоборот - проп окна тут не годится
        operationCancelOrder: false,
        // идёт запись по «Повторить сейчас» или льготное окно после ответа терминала: кнопка повтора ждёт итога
        retryingApply: false,
        // номер нажатия «Повторить сейчас»: ответ apply, пришедший после закрытия окна или повторного
        // открытия, - чужой, его операцию уже досматривает трекер или новый вход в окно
        applyAttempt: 0,
        showWaitingCloseConfirm: false,
        states: {
          refund: {
            type: 'refund',
            title: 'Вернуть платёж на карту через терминал?',
            icon: IconTerminal,
            buttonIcon: 'credit-card',
            buttonText: 'Да, вернуть',
          },
          loading: {
            type: 'loading',
            title: 'Возвращаем средства через терминал...',
            icon: IconLoading,
            buttonText: 'Средства возвращены',
          },
          error: {
            type: 'error',
            title: 'Терминал не вернул средства',
            icon: IconClose,
            buttonIcon: 'redo-alt',
            buttonText: 'Повторить возврат',
          },
          manual: {
            type: 'manual',
            title: 'Возврат вручную',
            icon: IconAlertCircle,
            buttonIcon: 'check',
            buttonText: 'Средства возвращены',
          },
          applying: {
            type: 'applying',
            title: 'Деньги возвращены клиенту',
            icon: IconAlertCircle,
            buttonIcon: 'redo-alt',
            buttonText: 'Повторить сейчас',
          },
        },
      }
    },
    computed: {
      ...mapState({
        currentEditOrder: state => state.orders.currentEditOrder,
      }),
      ...mapGetters({
        filialId: 'branches/getGlobalFilialId',
      }),
      targetOrder () {
        return this.order || this.currentEditOrder
      },
      showDialog: {
        get () { return this.value },
        set (newValue) { this.$emit('input', newValue) },
      },
      payment () {
        return this.targetOrder?.payment
      },
      isWaiting () {
        return this.currentState === this.states.loading
      },
      isManualState () {
        return this.currentState === this.states.manual
      },
      isApplyingState () {
        return this.currentState === this.states.applying
      },
      buttonDisabled () {
        return this.isLoading || this.pendingCancellation || this.isWaiting || this.retryingApply
      },
      // во время ожидания терминала закрыть можно (после подтверждения): команда доживёт на бэке, итог досмотрит трекер
      closeDisabled () {
        return this.pendingCancellation
      },
      // пока учёт пишется, ручной возврат - второй возврат тех же денег
      showManualButton () {
        return this.canRefundAutomatically && !this.isApplyingState
      },
      isKkmServerPaymentEnabled () {
        return Boolean(this.terminalPaymentSettings?.available)
      },
      // режим филиала «Только вручную»: бэк отдаёт refundAvailable = false и команду возврата не примет
      isTerminalRefundEnabled () {
        return this.terminalPaymentSettings?.refundAvailable !== false
      },
      canRefundAutomatically () {
        return this.isKkmServerPaymentEnabled &&
          this.isTerminalRefundEnabled &&
          this.payment?.type === PaymentType.Terminal &&
          Boolean(this.payment?.paymentId)
      },
      terminalPaymentSettings () {
        return this.paymentTypes.find(item => item.type === PaymentType.Terminal)?.settings
      },
      paymentSummary () {
        if (!this.payment) return null

        const typeName = PaymentType.getName(this.payment.type, this.targetOrder?.deliveryType, this.payment.mode).toLowerCase()

        return `Платёж ${typeName} на ${this.payment.total}${getCurrency()}`
      },
      description () {
        switch (this.currentState) {
          case this.states.loading:
            return this.operationType === 'refund'
              ? `Возврат на карту клиента. Терминал может запросить карту. ${this.terminalWaitingHint()}`
              : this.terminalWaitingHint()
          case this.states.error: return 'Попробуйте ещё раз или верните деньги клиенту сами, нажав «Вручную».'

          case this.states.manual:
            if (this.pendingOperationId) {
              return 'Терминал ещё выполняет возврат: ручной возврат станет доступен после его ответа, чтобы не вернуть деньги дважды.'
            }
            if (this.manualAfterFailure) {
              return 'Возврат через терминал не удался, это будет отмечено в платеже. Верните деньги клиенту и подтвердите.'
            }
            return this.payment?.type === PaymentType.Terminal && this.isKkmServerPaymentEnabled && !this.isTerminalRefundEnabled
              ? 'Возврат через терминал отключён в настройках филиала. Верните деньги клиенту и подтвердите.'
              : 'Верните деньги клиенту и подтвердите.'

          case this.states.applying: {
            const text = this.operationCancelOrder
              ? 'Отмена заказа ещё записывается, система повторяет попытки.'
              : 'Отмена платежа ещё записывается, система повторяет попытки.'
            return this.applyError ? `${text} ${this.applyError}` : text
          }

          default: return null
        }
      },
      stateIcon () {
        return this.pendingCancellation ? IconLoading : this.currentState.icon
      },
    },
    watch: {
      async showDialog () {
        if (this.showDialog) {
          await this.fetchAvailablePaymentTypes()
          this.initState()
          await this.resumeActiveOperation()
        } else {
          this.handOffOperation()
          this.stopPolling()
          this.stopCountdown()
        }
      },
      // терминал ответил, пока висел вопрос: закрывать уже нечего спрашивать
      isWaiting (waiting) {
        if (!waiting) this.showWaitingCloseConfirm = false
      },
    },
    created () {
      this.initState()
    },
    beforeDestroy () {
      this.handOffOperation()
      this.stopPolling()
    },
    methods: {
      initState () {
        this.stopPolling()
        this.stopCountdown()
        this.pendingOperationId = null
        this.manualAfterFailure = false
        this.operationType = null
        this.applyError = null
        this.operationCancelOrder = this.cancelOrder
        this.retryingApply = false
        this.applyAttempt += 1
        this.showWaitingCloseConfirm = false
        this.currentState = this.canRefundAutomatically ? this.states.refund : this.states.manual
      },
      onCloseClick () {
        if (this.isWaiting) {
          this.showWaitingCloseConfirm = true
          return
        }
        this.close()
      },
      confirmCloseWhileWaiting () {
        this.showWaitingCloseConfirm = false
        if (this.isWaiting) this.close()
      },
      close () {
        if (this.isWaiting) {
          this.showInfoMessage('Терминал ещё выполняет возврат. Итог придёт уведомлением, карточка заказа обновится сама.')
        }
        this.showDialog = false
      },
      // возврат, начатый в прошлый раз и ещё не завершённый: ждём его, а не шлём новый. Ищем и при выключенном
      // авто-возврате (филиал переключили в «Только вручную», выключили эквайринг, не загрузились настройки):
      // записи терминал не нужен, а ручной возврат поверх идущего или незаписанного - второй возврат тех же денег
      async resumeActiveOperation () {
        if (this.payment?.type !== PaymentType.Terminal) return

        const startState = this.currentState
        let operation = null
        this.isLoading = true
        try {
          operation = await paymentApi.fetchActiveTerminalOperation(this.targetOrder.id)
        } catch (e) {
          // без неё окно начнёт с вопроса о возврате, повторный возврат бэк не пустит
        } finally {
          this.isLoading = false
        }

        if (!this.showDialog || !['cancel', 'refund'].includes(operation?.type) || this.currentState !== startState) {
          return
        }

        untrack(operation.operationId)
        this.operationType = operation.type
        this.rememberOperationCancelOrder(operation)

        // терминал уже вернул деньги, учёт ещё пишется: сразу «ещё записывается», терминал не ждём
        if (operation.status === 'success' && operation.applied === false) {
          this.enterApplying(operation.operationId, operation)
          return
        }

        this.currentState = this.states.loading
        this.pendingOperationId = operation.operationId
        this.startCountdown(operation.timeoutSeconds)
        await this.watchOperation(operation.operationId)
      },
      // окно закрыли, не дождавшись итога: итог покажет и карточку обновит фоновый опрос
      handOffOperation () {
        if (!this.pendingOperationId) return

        const order = this.targetOrder?.code ? `Заказ №${this.targetOrder.code}: ` : ''
        trackInBackground({
          orderId: this.targetOrder?.id,
          operationId: this.pendingOperationId,
          expectedPaymentStatus: 'refund',
          successText: `${order}${this.handOffSuccessText()}`,
          // текст «записывается» - по флагу операции из ответа, а не по пропу окна
          unappliedText: operation => `${order}деньги возвращены, ${operation.cancelOrder ? 'отмена заказа' : 'отмена платежа'} записывается - проверьте заказ позже`,
          failText: `${order}терминал не вернул деньги`,
        })
        this.pendingOperationId = null
      },
      // флаг операции, а не проп: окно могло досматривать возврат, начатый другой кнопкой
      handOffSuccessText () {
        if (this.operationCancelOrder) return 'платёж возвращён, заказ отменён'

        return this.operationType === 'refund' ? 'возврат на карту оформлен' : 'деньги возвращены на карту'
      },
      stopPolling () {
        if (this.poller) {
          this.poller.cancel()
          this.poller = null
        }
      },
      // настройки терминала — филиала заказа, как в окне оплаты; глобальный филиал может быть другим
      async fetchAvailablePaymentTypes () {
        this.isLoading = true
        try {
          this.paymentTypes = await paymentApi.fetchTypes(this.targetOrder?.filialId || this.filialId)
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          this.isLoading = false
        }
      },
      toggleManualMode () {
        this.isManualState ? this.switchToAuto() : this.switchToManual()
      },
      // в ручной режим только по кнопке оператора: провал терминала оставляет в состоянии ошибки
      switchToManual () {
        this.manualAfterFailure = this.currentState === this.states.error
        this.stopPolling()
        this.currentState = this.states.manual
      },
      // назад к терминалу: незавершённую операцию не дублируем, а снова ждём её результат
      switchToAuto () {
        this.manualAfterFailure = false

        if (this.pendingOperationId) {
          this.currentState = this.states.loading
          this.watchOperation(this.pendingOperationId)
          return
        }

        this.currentState = this.states.refund
      },
      executeAction () {
        switch (this.currentState) {
          case this.states.refund:
          case this.states.error: this.cancelPayment(); break

          case this.states.applying: this.retryApply(); break

          case this.states.manual: this.confirmCancel(this.manualAfterFailure
            ? { refundType: RefundType.Manual, terminalRefundFailed: true }
            : { refundType: RefundType.Manual }); break
        }
      },
      async cancelPayment () {
        if (!this.canRefundAutomatically) {
          this.showErrorMessage('Возврат через терминал недоступен')
          return
        }

        this.currentState = this.states.loading

        const error = await this.cancelPointError()
        if (error) {
          this.showErrorMessage(error)
          this.currentState = this.canRefundAutomatically ? this.states.refund : this.states.manual
          return
        }

        try {
          const { operationId, timeoutSeconds, type } = await paymentApi.terminalCancel(this.targetOrder.id, { cancelOrder: this.cancelOrder })
          this.pendingOperationId = operationId
          this.operationType = type
          this.operationCancelOrder = this.cancelOrder
          // окно закрыли, пока команда создавалась
          if (!this.showDialog) {
            this.handOffOperation()
            return
          }
          this.startCountdown(timeoutSeconds)
          await this.watchOperation(operationId)
        } catch (e) {
          // 4xx: возврат отклонён до команды терминалу (колл-центр, запрет сотрудника, заказ уже отменён,
          // другой оплаченный платёж) - терминал не вызывался, это не его сбой: без «Повторить возврат»
          // и без отметки о провале, окно возвращается к началу, как при открытии
          if (this.isRejectedByServer(e)) {
            this.showErrorMessage(this.getErrorMessageFromResponse(e))
            this.currentState = this.canRefundAutomatically ? this.states.refund : this.states.manual
            return
          }
          this.handleError(this.getErrorMessageFromResponse(e) || e?.message || 'Неизвестная ошибка терминала')
        }
      },
      // сервер ответил отказом 4xx: запрос разобран и отклонён, ничего не создано
      isRejectedByServer (e) {
        const status = e?.response?.status

        return status >= 400 && status < 500
      },
      async watchOperation (operationId) {
        this.poller = waitForTerminalOperation(() => paymentApi.fetchTerminalOperation(this.targetOrder.id, operationId), {
          // терминал уже вернул деньги, учёт ещё пишется: льготное окно поллера идёт в «ещё записывается» -
          // без «Вручную» и без ложного «Терминал не вернул средства» на сбое сети; повтор ждёт итога окна
          onTick: operation => {
            if (operation.status !== 'success' || operation.applied !== false || this.isApplyingState) return

            this.stopCountdown()
            this.enterApplying(operationId, operation)
            this.retryingApply = true
          },
        })

        try {
          const operation = await this.poller.promise
          this.handleOperationResult(operation)
        } catch (e) {
          this.handleError(this.getErrorMessageFromResponse(e) || e?.message || 'Неизвестная ошибка терминала')
        }
      },
      handleOperationResult (operation) {
        this.poller = null
        this.stopCountdown()
        this.rememberOperationCancelOrder(operation)

        // терминал вернул деньги, учёт не записан за льготное окно: операцию держим - её повторяют кнопкой,
        // а если окно закроют, досматривает фоновый трекер
        if (operation.status === 'success' && operation.applied === false) {
          this.enterApplying(operation.id, operation)
          return
        }

        this.pendingOperationId = null

        if (operation.status === 'success' && operation.paymentStatus === 'refund') {
          // бэк уже перевёл платёж в refund. Сбой отмены после возврата сюда не доходит (возврат откатывается,
          // окно в «ещё записывается»); заказ не отменён при записанном возврате, только если окно отмены
          // заказа досмотрело возврат без отмены, начатый кнопкой «Отменить платёж»
          if (this.cancelOrder && operation.orderStatus !== 'canceled') {
            this.showErrorMessage(operation.error || 'Платёж возвращён, но заказ не отменён — отмените его вручную')
            this.$emit('canceled', { orderCanceled: false })
            this.showDialog = false
            return
          }

          this.showSuccessMessage(this.successMessage(this.operationType === 'refund'))
          this.$emit('canceled', { orderCanceled: this.cancelOrder })
          this.showDialog = false
          return
        }

        if (operation.status === 'success') {
          this.handleError(operation.error || 'Терминал подтвердил возврат, но платёж не отменён — проверьте заказ')
          return
        }

        this.handleError(operation.error || 'Неизвестная ошибка терминала')
      },
      successMessage (isRefund) {
        if (this.operationCancelOrder) {
          return 'Средства возвращены, заказ отменён'
        }
        return isRefund
          ? 'Возврат оформлен, деньги вернутся на карту в течение нескольких дней'
          : 'Средства возвращены на карту'
      },
      handleError (errorText) {
        this.poller = null
        this.stopCountdown()
        this.retryingApply = false
        this.showErrorMessage(errorText)

        // деньги уже вернулись, учёт пишется: сбой опроса не делает возврат «неудавшимся» - остаёмся
        // в «ещё записывается», операция сохраняется для повтора и фонового трекера. Итог, который бэк
        // уже записал (handleOperationResult обнулил операцию), здесь не держим - иначе окно застрянет
        if (this.isApplyingState && this.pendingOperationId) return

        this.pendingOperationId = null
        // авто-возврат выключен (досматривали операцию, начатую до переключения): «Повторить возврат» не пройдёт -
        // сразу ручной возврат с отметкой о провале терминала
        if (!this.canRefundAutomatically) {
          this.manualAfterFailure = true
          this.currentState = this.states.manual
          return
        }
        this.currentState = this.states.error
      },
      // флаг отмены заказа из ответа бэка (операция или active); старый бэк без поля - остаётся прежний
      rememberOperationCancelOrder ({ cancelOrder }) {
        if (typeof cancelOrder === 'boolean') this.operationCancelOrder = cancelOrder
      },
      // operation - ответ бэка (операция или active): error - причина последней неудачной записи.
      // Ручной повтор причину в операцию не пишет: свежую из его 422 не затираем пустой
      enterApplying (operationId, operation) {
        this.pendingOperationId = operationId
        this.applyError = operation.error || this.applyError
        this.rememberOperationCancelOrder(operation)
        this.retryingApply = false
        this.isLoading = false
        this.currentState = this.states.applying
      },
      // «Повторить сейчас»: запись по кнопке, итог забирает поллер с тем же льготным окном, что и после
      // терминала - записалось: окно закроется с успехом, нет - снова «ещё записывается»
      async retryApply () {
        const operationId = this.pendingOperationId
        if (!operationId) return

        const attempt = ++this.applyAttempt
        this.retryingApply = true
        let error = null
        try {
          await paymentApi.applyTerminalOperation(this.targetOrder.id, operationId)
        } catch (e) {
          error = e
        }

        // окно закрыли или открыли заново, пока шёл apply: второй опрос той же операции дал бы двойной итог
        if (attempt !== this.applyAttempt || !this.showDialog || this._isDestroyed) return

        if (error) {
          // бэк вернул причину (422): показываем её в окне, повторы в фоне продолжаются.
          // Сеть или 5xx - не причина записи: только снекбар, текст в окне прежний
          if (this.isRejectedByServer(error)) {
            this.applyError = this.getErrorMessageFromResponse(error) || this.applyError
          } else {
            this.showErrorMessage(this.getErrorMessageFromResponse(error) || 'Не удалось повторить запись')
          }
        }

        await this.watchOperation(operationId)
      },
      // возврат с отменой заказа - проверка ККМ на ПК точки, как у orders/{id}/cancel
      cancelPointError () {
        return this.cancelOrder ? orderCancelPointError(this.$store.getters) : null
      },
      async confirmCancel (data) {
        this.pendingCancellation = true
        try {
          const error = await this.cancelPointError()
          if (error) {
            this.showErrorMessage(error)
            return
          }

          await paymentApi.cancel(this.targetOrder.id, { ...data, cancelOrder: this.cancelOrder })
          if (this.cancelOrder) {
            this.showSuccessMessage('Средства возвращены, заказ отменён')
          }
          this.$emit('canceled', { orderCanceled: this.cancelOrder })
          this.showDialog = false
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          this.pendingCancellation = false
        }
      },
    },
  }
</script>

<style lang="less" scoped>
// оболочка, заголовок, подзаголовок и кнопки — из глобальных стилей PrimaryConfirmDialog
.cancel-payment-dialog-content {
  background-color: #fff;
  padding: 40px 32px 32px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
}
.cancel-payment-dialog {
  &_description {
    font-size: 18px;
    line-height: 1.4;
    text-align: center;
    color: #6b6b6b;
  }

  &_actions {
    display: flex;
    gap: 12px;
    margin-top: 40px;

    .close-button {
      flex: 1 1 0;

      &__compact {
        flex: 0 0 64px;
        min-width: 64px !important;
        padding: 0 !important;
      }
    }
    .manual-button {
      flex: 1 1 0;
      min-width: 0;
    }
    // v-btn ставит nowrap: с переключателем в ряду подпись подтверждения не влезала и обрезалась
    .confirm-button {
      flex: 2 1 0;
      min-width: 0;
      white-space: normal;
    }
  }
}
</style>
